import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Clock } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useAuthStore } from '@/stores/authStore'
import { list_cabinet_subscriptions } from '@/api/cabinet'
import type { Subscription } from '@/types/subscription'

const EXPIRING_SOON_DAYS = 7

const toMs = (v?: string | number | null) =>
  v == null ? null : typeof v === 'number' ? v * 1000 : new Date(v).getTime()

/** Cabinet-admin notice for past_due, expiring-soon and awaiting-validation subscriptions. */
export function SubscriptionBanner() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.auth.user)
  const isCabinetAdmin = user?.is_admin === true && user?.role !== 'Super Admin' && user?.role_name !== 'Super Admin'

  const { data } = useQuery({
    queryKey: ['cabinet-subscriptions', 'banner'],
    queryFn: () => list_cabinet_subscriptions({ page_size: 10 }),
    enabled: isCabinetAdmin,
    staleTime: 5 * 60 * 1000,
  })

  if (!isCabinetAdmin || !data) return null
  const subs: Subscription[] = data.results ?? []
  const current = subs.find((s) => ['active', 'past_due', 'trialing'].includes(s.status))
  const pending = subs.some((s) => s.status === 'pending')

  let variant: 'default' | 'destructive' = 'default'
  let title = ''
  let body = ''
  let Icon = Clock

  if (current?.status === 'past_due') {
    variant = 'destructive'
    Icon = AlertTriangle
    title = t('subscription_past_due') || 'Subscription payment overdue'
    body = t('subscription_past_due_desc') || 'Your subscription has expired. Renew now to keep access to your cabinet.'
  } else if (current?.status === 'active') {
    const end = toMs(current.current_period_end)
    const days = end == null ? null : Math.ceil((end - Date.now()) / 86_400_000)
    if (days != null && days <= EXPIRING_SOON_DAYS && !pending) {
      title = t('subscription_expiring_soon') || 'Subscription ending soon'
      body = (t('subscription_expiring_soon_desc', { days }) as string) || `Your subscription ends in ${days} day(s).`
    }
  } else if (!current && pending) {
    title = t('payment_awaiting_validation') || 'Payment awaiting validation'
    body = t('payment_awaiting_validation_desc') || 'We activate your subscription once your bank transfer is received and validated.'
  }

  if (!title) return null

  return (
    <div className='px-4 pt-4'>
      <Alert variant={variant}>
        <Icon className='h-4 w-4' />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          {body}{' '}
          <Link to='/cabinet-subscriptions' className='font-medium underline'>
            {t('manage_subscription') || 'Manage subscription'}
          </Link>
        </AlertDescription>
      </Alert>
    </div>
  )
}
